// client/src/components/ADMIN/AdminMerchantGestionViewer.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { firestore } from '../../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  limit as qLimit,
  documentId,
} from 'firebase/firestore';
import AdminNavbar from './AdminNavbar';
import '../CSS/Dashboard.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseFlexibleNumber = (val) => {
  if (val === null || val === undefined) return 0;
  const s = String(val).replace(',', '.').trim();
  const n = parseFloat(s);
  return isFinite(n) ? n : 0;
};

const formatPts = (n) =>
  parseFlexibleNumber(n).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const WD = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const MO = ['jan', 'fev', 'mar', 'avr', 'mai', 'jun', 'jul', 'aou', 'sep', 'oct', 'nov', 'dec'];
const formatCompactDateFR = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
  const wd = WD[dateObj.getDay()];
  const d = dateObj.getDate();
  const m = MO[dateObj.getMonth()];
  const y = dateObj.getFullYear();
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const mm = String(dateObj.getMinutes()).padStart(2, '0');
  return `${wd} ${d} ${m} ${y} ${hh}:${mm}`;
};

// ─── Composant ────────────────────────────────────────────────────────────────
const AdminMerchantGestionViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Le UID du merchant est passé via navigate state depuis AdminUsersViewer
  const merchantUid = location.state?.merchantUid;
  const merchantName = location.state?.merchantName || merchantUid;

  const [fidSettings, setFidSettings] = useState({ Type: null, multiscan: false, gift: '', multiple: 5, pointsPaliers: [] });
  const [fidelityActive, setFidelityActive] = useState(null); // null=chargement, true/false
  const [fidelityHistory, setFidelityHistory] = useState([]);
  const [userPseudos, setUserPseudos] = useState({});
  const [showAll, setShowAll] = useState(false);

  // ─── Paramètres fidélité ──────────────────────────────────────────────────
  useEffect(() => {
    if (!merchantUid) return;
    (async () => {
      const fidSnap = await getDoc(doc(firestore, 'fidelity', merchantUid));
      if (fidSnap.exists()) {
        const data = fidSnap.data();
        const multiscan = data.multiscan === 'yes';
        const type = data.Type;
        const gift = data.gift || '';
        const multiple = parseInt(data.multiple ?? 5, 10) || 5;
        if (type === 'Fidbypoints') {
          const levels = data.levels || [
            { points: data.points1, gift: data.gift1 },
            { points: data.points2, gift: data.gift2 },
            { points: data.points3, gift: data.gift3 },
          ].filter(p => p.points > 0);
          setFidSettings({ multiscan, Type: type, gift, multiple, pointsPaliers: levels });
        } else {
          setFidSettings({ multiscan, Type: type, gift, multiple, pointsPaliers: [] });
        }
      }
    })();
  }, [merchantUid]);

  // ─── Statut fidélité active (collection entreprises) ─────────────────────
  useEffect(() => {
    if (!merchantUid) return;
    (async () => {
      const entrepriseRef = collection(firestore, 'entreprises');
      const qStripe = query(entrepriseRef, where('userId', '==', merchantUid), qLimit(50));
      const stripeSnap = await getDocs(qStripe);
      if (stripeSnap.empty) { setFidelityActive(false); return; }
      let active = false;
      stripeSnap.forEach((d) => { if (d.data().fidelity === 'active') active = true; });
      setFidelityActive(active);
    })();
  }, [merchantUid]);

  // ─── Historique en temps réel ─────────────────────────────────────────────
  useEffect(() => {
    if (!merchantUid) return;
    const listCollectionRef = collection(firestore, 'fidelity', merchantUid, 'list');
    const unsubscribe = onSnapshot(listCollectionRef, (snapshot) => {
      const rows = snapshot.docs.map(docSnap => {
        const data = docSnap.data() || {};
        const docId = docSnap.id;
        return { ...data, docId, id: docId };
      });
      setFidelityHistory(rows);
    });
    return () => unsubscribe();
  }, [merchantUid]);

  // ─── Flatten historique ───────────────────────────────────────────────────
  const flattenedHistory = useMemo(() => {
    const toDate = (raw) => raw?.toDate ? raw.toDate() : new Date(raw || 0);
    const rows = fidelityHistory.flatMap((item) => {
      const docId = item.docId || item.clientUid || item.id;
      if (!docId) return [];
      const baseRow = {
        customerId: docId,
        docId,
        clientUid: item.clientUid || docId,
        customerLabel: item.clientPseudo || item.customerName || item.nom || '',
      };
      const timestamps = (item.timestamps || []).map((ts, i) => ({
        ...baseRow,
        id: `${docId}-ts-${i}`, date: toDate(ts.date), uid: ts.uid || '', type: 'timestamp', count: ts.count
      }));
      const resetstamps = (item.resetstamp || []).map((rs, i) => ({
        ...baseRow,
        id: `${docId}-rs-${i}`, date: toDate(rs.date), uid: rs.uid || '', type: 'resetstamp', count: rs.count, giftAtTime: rs.giftAtTime
      }));
      const pointshistory = (item.pointshistory || []).map((ph, i) => ({
        ...baseRow,
        id: `${docId}-ph-${i}`, date: toDate(ph.date), uid: ph.uid || '', type: 'pointshistory', points: ph.points, montant: ph.montant, gift: ph.gift
      }));
      return [...timestamps, ...resetstamps, ...pointshistory];
    });
    rows.sort((a, b) => b.date - a.date);
    return rows;
  }, [fidelityHistory]);

  // ─── Pseudos des caisses ──────────────────────────────────────────────────
  const loadingPseudosRef = useRef(new Set());
  useEffect(() => {
    const uniqueUids = Array.from(new Set(flattenedHistory.map(r => r.uid))).filter(Boolean);
    const missing = uniqueUids.filter(uid => !(uid in userPseudos) && !loadingPseudosRef.current.has(uid));
    if (missing.length === 0) return;
    missing.forEach(uid => loadingPseudosRef.current.add(uid));
    const chunks = [];
    for (let i = 0; i < missing.length; i += 10) chunks.push(missing.slice(i, i + 10));
    (async () => {
      try {
        const results = {};
        for (const group of chunks) {
          const qUsers = query(collection(firestore, 'users'), where(documentId(), 'in', group));
          const snap = await getDocs(qUsers);
          snap.forEach(d => { results[d.id] = d.data()?.pseudo || d.id; });
          group.forEach(uid => { if (!(uid in results)) results[uid] = uid; });
        }
        setUserPseudos(prev => ({ ...prev, ...results }));
      } finally {
        missing.forEach(uid => loadingPseudosRef.current.delete(uid));
      }
    })();
  }, [flattenedHistory, userPseudos]);

  // ─── Stats globales ───────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const day = (now.getDay() + 6) % 7;
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
    startOfWeek.setHours(0, 0, 0, 0);

    let scansToday = 0, scansWeek = 0, scansTotal = 0;
    let ptsToday = 0, ptsWeek = 0, ptsTotal = 0;

    for (const row of flattenedHistory) {
      const d = row.date instanceof Date ? row.date : new Date(row.date);
      if (row.type === 'timestamp' || row.type === 'resetstamp') {
        const count = parseInt(row.count || 1, 10) || 1;
        scansTotal += count;
        if (d >= startOfToday) scansToday += count;
        if (d >= startOfWeek) scansWeek += count;
      }
      if (row.type === 'pointshistory' && parseFlexibleNumber(row.points) > 0) {
        const pts = parseFlexibleNumber(row.points);
        ptsTotal += pts;
        if (d >= startOfToday) ptsToday += pts;
        if (d >= startOfWeek) ptsWeek += pts;
      }
    }
    return { scansToday, scansWeek, scansTotal, ptsToday, ptsWeek, ptsTotal };
  }, [flattenedHistory]);

  const suffix = fidSettings.Type === 'Fidbypoints' ? ' pts' : ' scans';
  const isPoints = fidSettings.Type === 'Fidbypoints';

  if (!merchantUid) {
    return (
      <div>
        <AdminNavbar />
        <div className="dashboard-container" style={{ textAlign: 'center', paddingTop: '40px' }}>
          <p>UID du commerçant manquant.</p>
          <button className="button-classic" onClick={() => navigate(-1)}>Retour</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminNavbar />
      <div className="dashboard-container">
        <style>{`
  .mgv-page { width: 100%; display: flex; flex-direction: column; gap: 20px; }
  .mgv-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  .dashboard-container .mgv-title { margin: 0; font-size: 26px; font-weight: 700; letter-spacing: -0.02em; text-align: left; color: var(--eb-text); }
  .mgv-uid { margin: 4px 0 0; font-family: var(--eb-font-mono); font-size: 12.5px; color: var(--eb-muted); overflow-wrap: anywhere; }
  .mgv-badges { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
  .table-fidelity-wrap { width: 100%; }
  .mgv-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); background: var(--eb-surface); box-shadow: var(--eb-shadow-sm); }
  .table-fidelity { width: 100%; min-width: 620px; border-collapse: collapse; background: var(--eb-surface); }
  .table-fidelity thead th { background: var(--eb-surface-2); color: var(--eb-muted); font-weight: 600; font-size: 12.5px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid var(--eb-border); }
  .table-fidelity th, .table-fidelity td { padding: 12px 16px; text-align: left; vertical-align: middle; word-break: break-word; font-size: 14px; color: var(--eb-text-2); }
  .table-fidelity tbody tr { border-bottom: 1px solid var(--eb-border); }
  .table-fidelity tbody tr:last-child { border-bottom: 0; }
  .table-fidelity tbody tr:hover { background: var(--eb-brand-50); }
  .table-fidelity .td-date { color: var(--eb-muted); font-size: 13.5px; white-space: nowrap; }
  .table-fidelity .td-muted { color: var(--eb-muted); font-size: 13.5px; }
  .pill { display: inline-flex; align-items: center; gap: 6px; font-weight: 700; padding: 3px 10px; border-radius: var(--eb-radius-pill); border: 1px solid transparent; font-size: 13px; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .pill-scan { background: var(--eb-brand-50); color: var(--eb-brand); border-color: var(--eb-brand-100); }
  .pill-gift { background: var(--eb-warning-bg); color: var(--eb-warning); border-color: #fde3b0; }
  .pill-pos  { background: var(--eb-success-bg); color: var(--eb-success); border-color: #c6f0da; }
  .pill-neg  { background: var(--eb-danger-bg); color: var(--eb-danger); border-color: #fbd0cb; }
  .row-gift { background: rgba(255, 250, 235, 0.6); }
  .row-in, .row-out { }
  .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .mgv-card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); padding: 20px 22px; box-shadow: var(--eb-shadow-sm); }
  .stat-card { display: flex; flex-direction: column; gap: 6px; }
  .stat-title { font-weight: 600; color: var(--eb-muted); font-size: 13.5px; }
  .stat-value { font-size: 26px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); }
  .mgv-empty { text-align: center; color: var(--eb-muted); padding: 32px 16px; margin: 0; font-size: 14px; }
  .mgv-more { text-align: center; margin-top: 12px; }
  @media (max-width: 640px) {
    .dashboard-container .mgv-title { font-size: 22px; }
    .stats-grid { gap: 8px; }
    .mgv-card.stat-card { padding: 12px 10px; }
    .stat-title { font-size: 12.5px; }
    .stat-value { font-size: 18px; }
    .mgv-head > .eb-btn { width: 100%; }
  }
        `}</style>

        <div className="mgv-page">
          {/* Header */}
          <div className="mgv-head">
            <div style={{ minWidth: 0 }}>
              <h1 className="mgv-title">Fidélité — {merchantName}</h1>
              <p className="mgv-uid">{merchantUid}</p>
              <div className="mgv-badges">
                {fidelityActive === null && (
                  <span className="eb-badge" style={{ background: 'var(--eb-surface-2)', color: 'var(--eb-muted)', borderColor: 'var(--eb-border)' }}>Chargement…</span>
                )}
                {fidelityActive === true && (
                  <span className="eb-badge eb-badge--success">✓ Fidélité active</span>
                )}
                {fidelityActive === false && (
                  <span className="eb-badge eb-badge--danger">✗ Fidélité inactive</span>
                )}
                {fidSettings.Type && (
                  <span className="eb-badge">
                    {isPoints ? 'Par points' : 'Par passages'}{fidSettings.multiscan ? ' · multiscan' : ''}
                  </span>
                )}
              </div>
            </div>
            <button className="eb-btn eb-btn--secondary" onClick={() => navigate(-1)}>← Retour</button>
          </div>

          {/* Statut inactif */}
          {fidelityActive === false && (
            <div className="eb-alert eb-alert--danger">
              Ce commerçant n'utilise pas encore la fidélité (non activée).
            </div>
          )}

          {/* Stats */}
          {fidelityActive === true && (
            <div className="stats-grid">
              <div className="mgv-card stat-card">
                <div className="stat-title">Aujourd'hui</div>
                <div className="stat-value">
                  {isPoints ? `${formatPts(stats.ptsToday)}${suffix}` : `${stats.scansToday}${suffix}`}
                </div>
              </div>
              <div className="mgv-card stat-card">
                <div className="stat-title">Cette semaine</div>
                <div className="stat-value">
                  {isPoints ? `${formatPts(stats.ptsWeek)}${suffix}` : `${stats.scansWeek}${suffix}`}
                </div>
              </div>
              <div className="mgv-card stat-card">
                <div className="stat-title">Total</div>
                <div className="stat-value">
                  {isPoints ? `${formatPts(stats.ptsTotal)}${suffix}` : `${stats.scansTotal}${suffix}`}
                </div>
              </div>
            </div>
          )}

          {/* Tableau historique */}
          <div className="table-fidelity-wrap">
            {flattenedHistory.length === 0 ? (
              <div className="mgv-card">
                <p className="mgv-empty">
                  {fidelityActive === null ? 'Chargement…' : 'Aucun historique de fidélité.'}
                </p>
              </div>
            ) : (
              <>
                <div className="mgv-table-scroll">
                  <table className="table-fidelity">
                    <thead>
                      <tr>
                        <th>{isPoints ? 'Points' : 'Scans'}</th>
                        <th>Date</th>
                        <th>Compte client</th>
                        <th>Caisse</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(showAll ? flattenedHistory : flattenedHistory.slice(0, 20)).map((row) => {
                        const d = row.date instanceof Date ? row.date : new Date(0);
                        let typeLabel = '';
                        let value = '';
                        let pillClass = 'pill';
                        let rowClassName = '';

                        if (row.type === 'resetstamp') {
                          typeLabel = `${row.giftAtTime || 'Gain'} 🎁`;
                          value = `×${row.count || 1}`;
                          pillClass += ' pill-gift';
                          rowClassName = 'row-gift';
                        } else if (row.type === 'pointshistory') {
                          if (parseFlexibleNumber(row.points) > 0) {
                            typeLabel = 'Achat 🛒';
                            value = `+${formatPts(row.points)} pts`;
                            pillClass += ' pill-pos';
                            rowClassName = 'row-in';
                          } else {
                            typeLabel = row.gift ? `${row.gift} 🎁` : 'Déduction 🎁';
                            value = `${formatPts(row.points)} pts`;
                            pillClass += ' pill-neg';
                            rowClassName = 'row-out';
                          }
                        } else {
                          typeLabel = 'Passage';
                          value = `+${row.count || 1}`;
                          pillClass += ' pill-scan';
                          rowClassName = 'row-in';
                        }

                        return (
                          <tr key={row.id} className={rowClassName}>
                            <td><span className={pillClass}>{value}</span></td>
                            <td className="td-date">{formatCompactDateFR(d)}</td>
                            <td>
                              {row.customerLabel || row.customerId?.slice(0, 8) || '—'}
                            </td>
                            <td className="td-muted">
                              {userPseudos[row.uid] || row.uid?.slice(0, 8) || '—'}
                            </td>
                            <td>{typeLabel}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {flattenedHistory.length > 20 && (
                  <div className="mgv-more">
                    <button className="eb-btn eb-btn--secondary" onClick={() => setShowAll(v => !v)}>
                      {showAll ? 'Voir moins' : `Voir tout (${flattenedHistory.length})`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMerchantGestionViewer;
